import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, List, ListItemButton, Paper, Popper, TextField, Typography } from "@mui/material";

interface FormulaEditorProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
}

const TOKEN_REGEX = /__([^_]+)__/g;
const FUNCTION_NAMES = ["SUM", "LAST"];

const TOKEN_STYLE =
  "background:#e3f2fd;border:1px solid #90caf9;border-radius:4px;padding:1px 6px;margin:0 2px;cursor:pointer;font-size:inherit;color:#1565c0;user-select:none;display:inline-block;vertical-align:baseline;";

const FUNCTION_TOKEN_STYLE =
  "background:#fff3e0;border:1px solid #ffb74d;border-radius:4px;padding:1px 6px;margin:0 2px;cursor:pointer;font-size:inherit;color:#e65100;user-select:none;display:inline-block;vertical-align:baseline;font-weight:600;";

function formulaToHTML(formula: string): string {
  // Replace measure/field tokens first
  let html = formula.replace(TOKEN_REGEX, (_, name) => {
    const safe = name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    return `<span data-token="${safe}" contenteditable="false" style="${TOKEN_STYLE}">${name}</span>`;
  });
  // Replace function names that precede (
  for (const fn of FUNCTION_NAMES) {
    html = html.replace(
      new RegExp(`\\b${fn}\\(`, "g"),
      `<span data-function="${fn}" contenteditable="false" style="${FUNCTION_TOKEN_STYLE}">${fn}</span>(`
    );
  }
  return html;
}

function serializeEditor(el: HTMLElement): string {
  let result = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += (node.textContent ?? "").replace(/​/g, "");
    } else if (node instanceof HTMLElement) {
      if (node.dataset.token !== undefined) {
        result += `__${node.dataset.token}__`;
      } else if (node.dataset.function !== undefined) {
        result += node.dataset.function;
      } else {
        node.childNodes.forEach(walk);
      }
    }
  };
  el.childNodes.forEach(walk);
  return result;
}

type PopperMode = "measure" | "function";

export const FormulaEditor: React.FC<FormulaEditorProps> = ({
  value,
  onChange,
  options,
  label = "Calculations",
  error,
  helperText
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const isPopperOpenRef = useRef(false);
  const editingTokenRef = useRef<HTMLElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const isInternalChange = useRef(false);

  const [popperOpen, setPopperOpen] = useState(false);
  const [popperMode, setPopperMode] = useState<PopperMode>("measure");
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [filter, setFilter] = useState("");
  const [focused, setFocused] = useState(false);

  const filteredOptions = useMemo(
    () => (filter ? options.filter((o) => o.toLowerCase().includes(filter.toLowerCase())) : options),
    [options, filter]
  );

  const openPopper = useCallback((anchor: Element, initialFilter: string, editingToken: HTMLElement | null) => {
    isPopperOpenRef.current = true;
    editingTokenRef.current = editingToken;
    setPopperMode("measure");
    setAnchorEl(anchor);
    setFilter(initialFilter);
    setPopperOpen(true);
    setTimeout(() => filterRef.current?.focus(), 50);
  }, []);

  const openFunctionPopper = useCallback((anchor: Element, fnSpan: HTMLElement) => {
    isPopperOpenRef.current = true;
    editingTokenRef.current = fnSpan;
    setPopperMode("function");
    setAnchorEl(anchor);
    setFilter("");
    setPopperOpen(true);
  }, []);

  const closePopper = useCallback((refocusEditor = true) => {
    isPopperOpenRef.current = false;
    editingTokenRef.current = null;
    savedRangeRef.current = null;
    setPopperOpen(false);
    setFilter("");
    if (refocusEditor) {
      setTimeout(() => editorRef.current?.focus(), 10);
    }
  }, []);

  const attachTokenHandlers = useCallback(() => {
    editorRef.current?.querySelectorAll<HTMLElement>("[data-token]").forEach((span) => {
      span.style.cssText = TOKEN_STYLE;
      span.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPopper(span, span.dataset.token ?? "", span);
      };
    });
    editorRef.current?.querySelectorAll<HTMLElement>("[data-function]").forEach((span) => {
      span.style.cssText = FUNCTION_TOKEN_STYLE;
      span.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openFunctionPopper(span, span);
      };
    });
  }, [openPopper, openFunctionPopper]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      attachTokenHandlers();
      return;
    }
    editorRef.current.innerHTML = formulaToHTML(value ?? "");
    attachTokenHandlers();
  }, [value, attachTokenHandlers]);

  const createTokenSpan = useCallback(
    (name: string): HTMLElement => {
      const span = document.createElement("span");
      span.dataset.token = name;
      span.contentEditable = "false";
      span.style.cssText = TOKEN_STYLE;
      span.textContent = name;
      span.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPopper(span, name, span);
      };
      return span;
    },
    [openPopper]
  );

  const createFunctionSpan = useCallback(
    (name: string): HTMLElement => {
      const span = document.createElement("span");
      span.dataset.function = name;
      span.contentEditable = "false";
      span.style.cssText = FUNCTION_TOKEN_STYLE;
      span.textContent = name;
      span.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openFunctionPopper(span, span);
      };
      return span;
    },
    [openFunctionPopper]
  );

  const handleSelect = useCallback(
    (name: string) => {
      const editor = editorRef.current;
      if (!editor) return;

      if (editingTokenRef.current) {
        const isFunction = editingTokenRef.current.dataset.function !== undefined;
        const newSpan = isFunction ? createFunctionSpan(name) : createTokenSpan(name);
        editingTokenRef.current.replaceWith(newSpan);
      } else if (savedRangeRef.current) {
        const range = savedRangeRef.current;
        const textNode = range.startContainer;
        if (textNode.nodeType === Node.TEXT_NODE) {
          const pos = range.startOffset;
          const text = textNode.textContent ?? "";
          textNode.textContent = text.slice(0, pos - 2) + text.slice(pos);
          const newSpan = createTokenSpan(name);
          const insertRange = document.createRange();
          insertRange.setStart(textNode, Math.max(0, pos - 2));
          insertRange.collapse(true);
          insertRange.insertNode(newSpan);
          if (!newSpan.nextSibling || newSpan.nextSibling.nodeType !== Node.TEXT_NODE) {
            newSpan.after(document.createTextNode("​"));
          }
          const sel = window.getSelection();
          if (sel) {
            const afterRange = document.createRange();
            afterRange.setStartAfter(newSpan);
            afterRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(afterRange);
          }
        }
      }

      isInternalChange.current = true;
      onChange(serializeEditor(editor));
      closePopper();
    },
    [createTokenSpan, createFunctionSpan, onChange, closePopper]
  );

  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        const text = textNode.textContent ?? "";
        const pos = range.startOffset;

        // Detect __ trigger for measure/field autocomplete
        if (pos >= 2 && text.slice(pos - 2, pos) === "__") {
          savedRangeRef.current = range.cloneRange();
          const caretRect = range.getBoundingClientRect();
          const anchor =
            caretRect.width > 0 || caretRect.height > 0
              ? ({ getBoundingClientRect: () => caretRect } as unknown as Element)
              : editor;
          openPopper(anchor, "", null);
          isInternalChange.current = true;
          onChange(serializeEditor(editor));
          return;
        }

        // Detect function trigger: known function name immediately before (
        if (pos >= 1 && text[pos - 1] === "(") {
          const beforeParen = text.slice(0, pos - 1);
          const match = beforeParen.match(/([A-Za-z]+)$/);
          if (match) {
            const fnUpper = match[1].toUpperCase();
            if (FUNCTION_NAMES.includes(fnUpper)) {
              const fnTextLength = match[1].length;
              const fnStart = pos - 1 - fnTextLength;
              // Remove the typed function name, keep the (
              textNode.textContent = text.slice(0, fnStart) + text.slice(pos - 1);
              // Insert function chip at fnStart
              const newSpan = createFunctionSpan(fnUpper);
              const insertRange = document.createRange();
              insertRange.setStart(textNode, fnStart);
              insertRange.collapse(true);
              insertRange.insertNode(newSpan);
              // Place cursor after (
              const remainingText = newSpan.nextSibling;
              if (remainingText && remainingText.nodeType === Node.TEXT_NODE) {
                const afterRange = document.createRange();
                afterRange.setStart(remainingText, 1);
                afterRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(afterRange);
              }
              isInternalChange.current = true;
              onChange(serializeEditor(editor));
              return;
            }
          }
        }
      }
    }

    isInternalChange.current = true;
    onChange(serializeEditor(editor));
  }, [onChange, openPopper, createFunctionSpan]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape" && popperOpen) {
        closePopper();
      }
    },
    [popperOpen, closePopper]
  );

  const handleFilterKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        closePopper();
      } else if (e.key === "Enter" && filteredOptions.length > 0) {
        e.preventDefault();
        handleSelect(filteredOptions[0]);
      }
    },
    [filteredOptions, handleSelect, closePopper]
  );

  const handleFilterBlur = useCallback(() => {
    setTimeout(() => {
      if (isPopperOpenRef.current) {
        closePopper(false);
      }
    }, 150);
  }, [closePopper]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      if (editorRef.current) {
        isInternalChange.current = true;
        onChange(serializeEditor(editorRef.current));
      }
    },
    [onChange]
  );

  const isLabelFloating = focused || popperOpen || !!value;

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        component="label"
        sx={{
          position: "absolute",
          top: isLabelFloating ? -9 : "16.5px",
          left: isLabelFloating ? 10 : 14,
          px: isLabelFloating ? 0.5 : 0,
          bgcolor: isLabelFloating ? "background.paper" : "transparent",
          color: error ? "error.main" : focused || popperOpen ? "primary.main" : "text.secondary",
          fontSize: isLabelFloating ? "0.75rem" : "1rem",
          lineHeight: isLabelFloating ? 1 : 1.5,
          transition: "top 0.15s, left 0.15s, font-size 0.15s",
          pointerEvents: "none",
          zIndex: 1,
          userSelect: "none"
        }}
      >
        {label}
      </Box>

      <Box
        ref={editorRef}
        component="div"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setTimeout(() => {
            if (!isPopperOpenRef.current) setFocused(false);
          }, 0);
        }}
        sx={{
          minHeight: "56px",
          padding: "16.5px 14px",
          border: `${focused || popperOpen ? 2 : 1}px solid`,
          borderColor: error ? "error.main" : focused || popperOpen ? "primary.main" : "rgba(0,0,0,0.23)",
          borderRadius: "4px",
          outline: "none",
          fontSize: "1rem",
          lineHeight: 1.5,
          fontFamily: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          cursor: "text",
          "&:hover": {
            borderColor: error ? "error.main" : focused || popperOpen ? "primary.main" : "rgba(0,0,0,0.87)"
          }
        }}
      />

      {helperText && (
        <Box
          component="span"
          sx={{
            display: "block",
            color: error ? "error.main" : "text.secondary",
            fontSize: "0.75rem",
            lineHeight: 1.66,
            mx: "14px",
            mt: "3px"
          }}
        >
          {helperText}
        </Box>
      )}

      {/* Backdrop for function selection popper — captures outside clicks */}
      {popperOpen && popperMode === "function" && (
        <Box
          sx={{ position: "fixed", inset: 0, zIndex: 1399 }}
          onClick={() => closePopper(false)}
        />
      )}

      <Popper open={popperOpen} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1400 }}>
        <Paper elevation={3} sx={{ display: "flex", flexDirection: "column", mt: 0.5, ...(popperMode === "measure" ? { width: 280, maxHeight: 300 } : { width: 150 }) }}>
          {popperMode === "measure" && (
            <Box sx={{ p: 1 }}>
              <TextField
                inputRef={filterRef}
                size="small"
                fullWidth
                placeholder="Search measures & fields..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={handleFilterKeyDown}
                onBlur={handleFilterBlur}
                autoComplete="off"
              />
            </Box>
          )}
          <List dense sx={{ overflow: "auto", flex: 1 }}>
            {popperMode === "measure" ? (
              filteredOptions.length === 0 ? (
                <Typography variant="body2" sx={{ p: 1.5, color: "text.secondary", textAlign: "center" }}>
                  No options found
                </Typography>
              ) : (
                filteredOptions.map((opt) => (
                  <ListItemButton key={opt} onMouseDown={(e) => e.preventDefault()} onClick={() => handleSelect(opt)}>
                    <Typography variant="body2">{opt}</Typography>
                  </ListItemButton>
                ))
              )
            ) : (
              FUNCTION_NAMES.map((fn) => (
                <ListItemButton
                  key={fn}
                  selected={editingTokenRef.current?.dataset.function === fn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(fn)}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e65100" }}>
                    {fn}
                  </Typography>
                </ListItemButton>
              ))
            )}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};
